import _cloneDeep from 'lodash/cloneDeep'
import { _patch } from 'frontcore/utils/communications/apiClient'

const URL_BASE_SEQUENCES = 'sequences'

export default function Sequence ({
  id,
  sequence_id,
  sequence_type = Sequence.sequenceTypes.regular,
  timeframe_value,
  ctc = false,
  operation_number,
  key_characteristics = [],
  standards = [],
  rsrs_characteristics = [],
  frequency_text,
  frequency_type,
  frequency,
  annotations = [],
  sequences_in_standard = [],
  media = null,
} = {}) {
  if (!Array.isArray(standards)) {
    throw new Error('standards should be an array')
  }
  this.id = id
  this.sequence_id = sequence_id
  this.sequence_type = sequence_type
  this.timeframe_value = timeframe_value
  this.ctc = ctc
  this.operation_number = operation_number
  this.standards = standards
  this.key_characteristics = key_characteristics
  this.rsrs_characteristics = rsrs_characteristics
  this.frequency = newFrequency({frequency_text, frequency, frequency_type, sequence_type})
  this.annotations = annotations
  this.sequences_in_standard = sequences_in_standard
  this.media = media
}

Object.defineProperties(Sequence, {
  typesOfFrequency: {
    writable: false,
    value: Object.freeze({
      atTurnBeginning: 0,
      atAGivenNumberOfCycles: 1,
      other: 2,
    }),
  },
  sequenceTypes: {
    writable: false,
    value: Object.freeze({
      regular: 0,
      periodic: 1,
    }),
  },
})

Sequence.prototype = Object.create(Sequence.prototype)
Object.assign(Sequence.prototype, {
  toApiPayload () {
    const {frequency, frequency_text, frequency_type} = getApiPayloadFrequencyProps.call(this)

    return {
      id: this.id,
      annotations: this.annotations.map(ann => ann.id ?? ann),
      ctc : this.ctc,
      frequency,
      frequency_text,
      frequency_type,
      key_characteristics : this.key_characteristics.map(kc => kc.id ?? kc),
      media: this.media?.id,
      operation_number : this.operation_number,
      rsrs_characteristics : this.rsrs_characteristics.map(rsrsItem => rsrsItem?.id || rsrsItem),
      sequence_id : this.sequence_id,
      sequence_type : this.sequence_type,
      standards : this.standards,
      timeframe_value : this.timeframe_value,
    }
  },
  clone () {
    return _cloneDeep(this)
  },
  async updateInServer (editingFromStandardId) {
    if (!editingFromStandardId) {
      throw new Error('Updating a sequence needs the id of the standard that is modifying it')
    }
    const url = `${URL_BASE_SEQUENCES}/${this.id}/`
    return new Sequence(await _patch(url, {...this.toApiPayload(editingFromStandardId), editingFromStandardId}))
  },
})

function newFrequency ({frequency_text, frequency, frequency_type, sequence_type}) {
  if (sequence_type === Sequence.sequenceTypes.periodic) {
    let value
    switch (frequency_type) {
    case Sequence.typesOfFrequency.atTurnBeginning:
      value = null
      break
    case Sequence.typesOfFrequency.atAGivenNumberOfCycles:
      value = frequency
      break
    case Sequence.typesOfFrequency.other:
      value = frequency_text
      break
    default:
      throw new Error(`frequency_type ${frequency_type} not registered`)
    }

    return {
      type: frequency_type,
      value,
    }
  }

  return null
}

function getApiPayloadFrequencyProps () {
  const frequencyPayloadProps = {
    frequency: null,
    frequency_text: '',
    frequency_type: undefined,
  }

  if (this.sequence_type === Sequence.sequenceTypes.regular) {
    return frequencyPayloadProps
  }

  frequencyPayloadProps.frequency_type = this.frequency.type

  if (this.frequency.type === Sequence.typesOfFrequency.atAGivenNumberOfCycles) {
    frequencyPayloadProps.frequency = this.frequency.value
  } else if (this.frequency.type === Sequence.typesOfFrequency.other) {
    frequencyPayloadProps.frequency_text = this.frequency.value
  }

  return frequencyPayloadProps
}
